import { memo, useCallback } from 'react';
import useBetBuilderModal from '../../features/bet-builder-modal/sportsbook-use-bet-builder-modal';
import { SportsbookTranslations } from '../../sportsbook-text-keys';
import { EventWithMarketsAndSelections, MarketWithSelections } from '../../sportsbook-types';
import styled from 'styled-components';
import GameCardMarket from './components/sportsbook-game-card-market';
import { GameCardWrapper, GameCardDetails, GAME_CARD_BREAKPOINT } from './components/sportsbook-game-card-base';
import { trackSportsbookNavigationToEventPageFromGameCard } from '@comeon/analytics';
import { isEqual } from '@comeon/utility';
import GameCardGameTime from './components/sportsbook-game-card-time';
import GameCardIcons from './components/sportsbook-game-card-icons';
import GameCardScoreboard from './components/sportsbook-game-card-scoreboard';
import { EventTags } from '../../api/sbapi/sbapi-api-model';

const StyledGameCardDetails = styled(GameCardDetails)`
	width: 100%;
	grid-column: 2;
	align-items: flex-start;
	justify-content: flex-end;
	gap: 6px;

	@container (min-width: ${GAME_CARD_BREAKPOINT}px) {
		grid-column: span 2;
		grid-template-columns: 1fr 1fr;
		justify-content: space-between;
	}
`;

const StyledGameCardWrapper = styled(GameCardWrapper)`
	@container (min-width: ${GAME_CARD_BREAKPOINT}px) {
		grid-template-columns: 1fr 1fr;
	}
`;

type GameCardProps = {
	event: EventWithMarketsAndSelections;
	tag: string;
	market?: MarketWithSelections;
	translations: SportsbookTranslations;
	shouldClearSelectionTitle?: boolean;
	onClick?: () => void;
	className?: string;
};

/**
 * Component for displaying a game card.
 * @param event - The event to display.
 * @param tag - The tag to determine origin of selection for tracking purposes.
 * @param market - The market to display.
 * @param translations - The translations.
 * @param shouldClearSelectionTitle - Control if the selection renderer should clear the selection title and leave it empty
 * @param onClick - Callback for when the game card is clicked.
 * @param className - Optional class name.
 */

const GameCard: React.FC<GameCardProps> = memo(
	({ event, tag, market, translations, shouldClearSelectionTitle, onClick, className }) => {
		const { hasBetBuilderForEvent } = useBetBuilderModal(event);
		const { isLive } = event;

		const handleEventLinkClicked = useCallback(() => {
			trackSportsbookNavigationToEventPageFromGameCard(event.id, market?.id || 'empty', event.isLive);
		}, [event, market]);

		return (
			<StyledGameCardWrapper className={className} data-at="game-card" onClick={onClick}>
				<StyledGameCardDetails>
					<GameCardGameTime
						isLive={isLive}
						startingOn={event.startingOn}
						lastUpdatedOn={event.lastUpdatedOn}
						liveGameState={event.liveGameState}
					/>
					<GameCardIcons
						hasBetBuilderForEvent={hasBetBuilderForEvent}
						hasSuperSub={event.tags.includes(EventTags.HAS_SUPER_SUB)}
						translations={translations}
					/>
				</StyledGameCardDetails>
				<GameCardScoreboard event={event} onClick={handleEventLinkClicked} />
				<GameCardMarket market={market} shouldClearSelectionTitle={shouldClearSelectionTitle} tag={tag} />
			</StyledGameCardWrapper>
		);
	},
	(prevProps, nextProps) => isEqual(prevProps, nextProps)
);

GameCard.displayName = 'GameCard';

export default GameCard;
